import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  useEffect, useState, useRef, useMemo,
} from 'react';
import { useNavigate, Link, matchPath } from 'react-router-dom';
import useLiterals from 'utils/hooks/useLiterals';
import Translate from 'components/Translate';
import { ROUTE_PATH } from 'routes';
import { onLogout } from 'modules/session/actions';
import useConfiguration from 'hooks/useConfiguration';
import { CMS_MESSAGES } from 'constants/cms';
import useCmsPreview from 'hooks/useCmsPreview';
import { formatLink } from 'utils/formatLink';
import { EFFECT_LIST_BOX_RIGHT_IN_VIEW } from 'constants/effects';
import LogoImg from 'assets/logos/logo.png';
import userImg from 'assets/images/default_avatar.jpg';
import { getCMSDocumentSrc } from 'utils/getCMSDocumentSrc';
import SETTINGS_SECTIONS from 'constants/settings';
import { PORTAL_URL } from 'constants/common';
import usePlatform from 'utils/hooks/usePlatform';
import { motion } from 'framer-motion';
import useKLU from 'hooks/useKLU';
import Header from './components/Header';
import BottomMenu from './components/BottomMenu';
import './styles.scss';

const Layout = (props) => {
  const {
    children,
    title = '',
    onBack = null,
    className = '',
    headerMenu = true,
    rightMenu = true,
    bgWhite = false,
    bottomMenu = false,
    fullHeight = false,
  } = props;

  const { rightMenu: literalsRM } = useLiterals('layout');
  const configuration = useSelector((state) => state.configuration);
  const [openRightMenu, setOpenRightMenu] = useState(false);
  const { user, authenticated } = useSelector((state) => state.session);
  const { getConfigurationValue } = useConfiguration();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const klu = useKLU();
  const { isWeb } = usePlatform();
  const [menuItems, setMenuItems] = useState([]);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const avatar = useMemo(() => (user?.avatar ? getCMSDocumentSrc({ hash: user.avatar }) : ''), [user?.avatar]);
  const { value: previewMessage } = useCmsPreview(CMS_MESSAGES.UPDATE_RIGHT_MENU);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (bgWhite) {
      document.body.classList.add('bg-white');
    }
    return () => {
      if (bgWhite) {
        document.body.classList.remove('bg-white');
      }
    };
  }, [bgWhite]);

  useEffect(() => {
    if (configuration.list.length) {
      setMenuItems(JSON.parse(getConfigurationValue('rightMenu') || '[]'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration]);

  useEffect(() => {
    if (previewMessage) {
      setMenuItems(previewMessage);
      setOpenRightMenu(true);
    }
  }, [previewMessage]);

  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [userMenuOpen]);

  const handleCloseRightMenu = () => {
    if (openRightMenu) {
      setOpenRightMenu(false);
    }
  };

  const handleLogout = () => {
    dispatch(onLogout()).then(() => {
      setOpenRightMenu(false);
      navigate(ROUTE_PATH.LOGIN, { state: { from: 'logout' } });
    });
  };

  const isActualPath = (path) => {
    const match = matchPath({
      path,
      exact: true,
      strict: false,
    }, window.location.pathname);
    return match ? 'active' : '';
  };

  return (
    <div className={`layout ${className} ${!headerMenu ? 'no-header' : ''} ${bottomMenu ? 'has-bottom-menu' : ''}`}>
      {headerMenu && (
        <Header
          title={title}
          onBack={onBack}
          rightMenu={rightMenu}
          openRightMenu={openRightMenu}
          onRightMenu={() => setOpenRightMenu(!openRightMenu)}
        />
      )}
      {/* Desktop top-nav: visible only on lg+ screens */}
      {headerMenu && (
        <nav className='desktop-nav hidden lg:flex'>
          <Link to={ROUTE_PATH.HOME} className='desktop-nav__logo'>
            <img src={LogoImg} className='desktop-nav__logo-img' alt='Platform logo' />
          </Link>
          <div className='desktop-nav__links justify-center'>
            <Link to={ROUTE_PATH.HOME} className={`desktop-nav__link ${isActualPath(ROUTE_PATH.HOME)}`}>
              <Translate literal={literalsRM.home} />
            </Link>
            {authenticated && (
              <>
                <Link to={ROUTE_PATH.PAYMENTS} className={`desktop-nav__link ${isActualPath(ROUTE_PATH.PAYMENTS)}`}>
                  <Translate literal={literalsRM.payments} />
                </Link>
                <Link to={ROUTE_PATH.RECEIPTS_HISTORY} className={`desktop-nav__link ${isActualPath(ROUTE_PATH.RECEIPTS_HISTORY)}`}>
                  <Translate literal={literalsRM.reprintingReceipts} />
                </Link>
                <Link to={ROUTE_PATH.LOYALTY_POINTS_COUPONS} className={`desktop-nav__link ${isActualPath(ROUTE_PATH.LOYALTY_POINTS_PROMOTIONS)}`}>
                  <Translate literal={literalsRM.myCoupons} />
                </Link>
              </>
            )}
          </div>
          <div className='desktop-nav__user' ref={userMenuRef}>
            <button
              type='button'
              className='desktop-nav__user-trigger'
              aria-haspopup='menu'
              aria-expanded={userMenuOpen}
              onClick={() => setUserMenuOpen((open) => !open)}
            >
              <span className='desktop-nav__avatar' style={avatar ? { backgroundImage: `url("${avatar}")` } : undefined}>
                {!avatar && <img src={userImg} alt='' />}
              </span>
              {user?.firstName && (
                <span className='desktop-nav__username'>{user.firstName}</span>
              )}
              <svg className='desktop-nav__chevron' viewBox='0 0 24 24' width='14' height='14' aria-hidden='true'>
                <path d='M6 9l6 6 6-6' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
              </svg>
            </button>
            {userMenuOpen && (
              <div className='desktop-nav__menu' role='menu'>
                <Link
                  to={ROUTE_PATH.setSettings()}
                  className='desktop-nav__menu-item'
                  role='menuitem'
                  onClick={() => setUserMenuOpen(false)}
                >
                  <Translate literal={literalsRM.settings} />
                </Link>
                {authenticated && (
                  <Link
                    to={ROUTE_PATH.PROFILE}
                    className='desktop-nav__menu-item'
                    role='menuitem'
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <Translate literal={literalsRM.profile} />
                  </Link>
                )}
                <Link
                  to={ROUTE_PATH.TERMS_AND_CONDITIONS}
                  className='desktop-nav__menu-item'
                  role='menuitem'
                  onClick={() => setUserMenuOpen(false)}
                >
                  <Translate literal={literalsRM.termsAndConditions} />
                </Link>
                {isWeb && (
                  <a
                    className='desktop-nav__menu-item'
                    role='menuitem'
                    href={PORTAL_URL}
                    target='_blank'
                    rel='noreferrer'
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <Translate literal={literalsRM.backToSite} />
                  </a>
                )}
                {authenticated && (
                  <button
                    type='button'
                    className='desktop-nav__menu-item desktop-nav__menu-item--danger'
                    role='menuitem'
                    onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                  >
                    {literalsRM.logout}
                  </button>
                )}
              </div>
            )}
          </div>
        </nav>
      )}
      <div className={`layout-wrapper ${openRightMenu ? 'show-right-menu' : ''}`}>
        <motion.div
          initial={null}
          animate={openRightMenu ? 'open' : 'default'}
          variants={{
            open: {
              position: 'absolute',
              left: 0,
              width: '65%',
              top: 0,
              x: -120,
              zoom: 0.65,
              border: '20px solid white',
              padding: '0 20px 20px 20px',
              boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.2), 0 0 30px rgba(0, 0, 0, 0.2)',
              borderRadius: 30,
              height: '120vh',
              overflow: 'hidden',
            },
            default: {
              position: 'relative',
              width: '100%',
              x: 0,
              height: 'unset',
            },
          }}
          className='layout-content'
          onClick={handleCloseRightMenu}
        >
          <div className={fullHeight ? 'full-height' : ''}>
            {children}
          </div>
        </motion.div>
        {openRightMenu && (
          // Right-side drawer: hidden on desktop (lg+), desktop uses inline nav instead
          <div className={`layout-right-menu lg:hidden ${user?.id ? 'with-user' : ''}`}>
            {user?.id && (
              <div className='mb-4'>
                <p className='mb-1'><b>{user.firstName}</b></p>
                {user?.clientId && <Translate className='text-sm text-neutral-500 truncate mb-1' literal={literalsRM.client} vars={{ number: user.clientId }} />}
                {klu.enabled && klu.account && (
                  <Link to={ROUTE_PATH.PROFILE_INTERBANK_KEY} className='text-sm'>
                    <Translate className='text-sm text-neutral-500 truncate mb-0' literal={literalsRM.interbankKey} vars={{ number: klu.account }} />
                  </Link>
                )}
                {!user?.clientId && <div className='mt-2'><a href={ROUTE_PATH.setSettings(SETTINGS_SECTIONS.SAP_ASSOCIATE)} className='text-sm'>{literalsRM.associate}</a></div>}
              </div>
            )}
            <nav>
              <div className='links'>
                <motion.div key='nav-home' custom={0} {...EFFECT_LIST_BOX_RIGHT_IN_VIEW}>
                  <Link to={ROUTE_PATH.HOME} onClick={() => handleCloseRightMenu()} className={`no-underline ${isActualPath(ROUTE_PATH.HOME)}`}>
                    <Translate className='h4 text-neutral-500 mb-6' literal={literalsRM.home} />
                  </Link>
                </motion.div>
                <motion.div key='nav-0' custom={0} {...EFFECT_LIST_BOX_RIGHT_IN_VIEW}>
                  <Link to={ROUTE_PATH.setSettings()} onClick={() => handleCloseRightMenu()} className={`no-underline ${isActualPath(ROUTE_PATH.SETTINGS)}`}>
                    <Translate className='h4 text-neutral-500 mb-6' literal={literalsRM.settings} />
                  </Link>
                </motion.div>
                {authenticated && (
                  <>
                    <motion.div key='nav-1' custom={1} {...EFFECT_LIST_BOX_RIGHT_IN_VIEW}>
                      <Link to={ROUTE_PATH.PROFILE} onClick={() => handleCloseRightMenu()} className={`no-underline ${isActualPath(ROUTE_PATH.PROFILE)}`}>
                        <Translate className='h4 text-neutral-500 mb-6' literal={literalsRM.profile} />
                      </Link>
                    </motion.div>
                    <motion.div key='nav-2' custom={2} {...EFFECT_LIST_BOX_RIGHT_IN_VIEW}>
                      <Link to={ROUTE_PATH.PAYMENTS} onClick={() => handleCloseRightMenu()} className={`no-underline ${isActualPath(ROUTE_PATH.PAYMENTS)}`}>
                        <Translate className='h4 text-neutral-500 mb-6' literal={literalsRM.payments} />
                      </Link>
                    </motion.div>
                    <motion.div key='nav-3' custom={3} {...EFFECT_LIST_BOX_RIGHT_IN_VIEW}>
                      <Link to={ROUTE_PATH.RECEIPTS_HISTORY} onClick={() => handleCloseRightMenu()} className={`no-underline ${isActualPath(ROUTE_PATH.RECEIPTS_HISTORY)}`}>
                        <Translate className='h4 text-neutral-500 mb-6' literal={literalsRM.reprintingReceipts} />
                      </Link>
                    </motion.div>
                    <motion.div key='nav-4' custom={3} {...EFFECT_LIST_BOX_RIGHT_IN_VIEW}>
                      <Link to={ROUTE_PATH.LOYALTY_POINTS_COUPONS} onClick={() => handleCloseRightMenu()} className={`no-underline ${isActualPath(ROUTE_PATH.LOYALTY_POINTS_COUPONS)}`}>
                        <Translate className='h4 text-neutral-500 mb-6' literal={literalsRM.myCoupons} />
                      </Link>
                    </motion.div>
                  </>
                )}
                {menuItems
                  .filter((item) => item.name)
                  .map((item, index, arr) => {
                    const link = formatLink(item.url);
                    const i = index + 5;
                    return (
                      <motion.div key={`nav-${i}`} custom={i} {...EFFECT_LIST_BOX_RIGHT_IN_VIEW}>
                        <Link key={index} to={link.to} target={link.target} onClick={() => handleCloseRightMenu()} className={`no-underline ${isActualPath(link.to)}`}>
                          <Translate className={`h4 text-neutral-500 ${index === arr.length - 1 ? 'mb-0' : 'mb-6'}`} literal={item.name} />
                        </Link>
                      </motion.div>
                    );
                  })}
              </div>
              {isWeb && (
                <motion.div key='nav-backsite' custom={1 + menuItems.length} {...EFFECT_LIST_BOX_RIGHT_IN_VIEW}>
                  <a href={PORTAL_URL} target='_blank' rel='noreferrer' onClick={() => handleCloseRightMenu()} className='no-underline'>
                    <Translate className='h4 text-neutral-500 mt-6 mb-0' literal={literalsRM.backToSite} />
                  </a>
                </motion.div>
              )}
              {authenticated && (
                <motion.div key='nav-logout' custom={2 + menuItems.length} {...EFFECT_LIST_BOX_RIGHT_IN_VIEW}>
                  <div className='h4 color-primary mt-6' onClick={handleLogout}>
                    {literalsRM.logout}
                  </div>
                </motion.div>
              )}
            </nav>
          </div>
        )}
      </div>
      {bottomMenu && <BottomMenu action={bottomMenu} />}
    </div>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  onBack: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  title: PropTypes.string,
  className: PropTypes.string,
  headerMenu: PropTypes.bool,
  rightMenu: PropTypes.bool,
  bottomMenu: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  bgWhite: PropTypes.bool,
  fullHeight: PropTypes.bool,
};

export default Layout;
